import React, { useState } from 'react'
import { client } from '@Utils'
import { simulationEditorEndpoints as endpoints } from '@URLS'

export const AISimulationGeneratorPanel = ({ coursenum, importButtonLabel }) => {
  const [expanded, setExpanded] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [pluginHint, setPluginHint] = useState('')
  const [loading, setLoading] = useState(false)
  const [warnings, setWarnings] = useState([])
  const [error, setError] = useState(null)
  const [generatedDraft, setGeneratedDraft] = useState(null)
  const [copied, setCopied] = useState(false)

  const handleGenerate = async () => {
    if (!prompt.trim()) return
    setLoading(true)
    setError(null)
    setWarnings([])
    setGeneratedDraft(null)
    setCopied(false)
    try {
      const data = new FormData()
      data.append('prompt', prompt)
      if (pluginHint) data.append('plugin_hint', pluginHint)
      const response = await client.post(endpoints.generate({ coursenum }), data)
      if (response.data.success) {
        setGeneratedDraft(response.data.simulation)
        setWarnings(response.data.warnings || [])
        const importSection = document.getElementById('sim-import-section')
        if (importSection) {
          importSection.style.display = ''
        }
      } else {
        setError(response.data.error || 'Generation failed.')
      }
    } catch (e) {
      const errMsg = e.response && e.response.data && e.response.data.error
        ? e.response.data.error
        : 'Error connecting to AI service.'
      setError(errMsg)
    } finally {
      setLoading(false)
    }
  }

  const focusImportSection = () => {
    const importSection = document.getElementById('sim-import-section')
    if (!importSection) return
    importSection.style.display = ''
    importSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleImport = () => {
    if (!generatedDraft) return

    const importForm = document.getElementById('sim-import-form')
    const importTextarea = document.getElementById('sim-import-textarea')

    if (!importForm || !importTextarea) {
      focusImportSection()
      return
    }

    importTextarea.value = JSON.stringify(generatedDraft, null, 2)
    importTextarea.dispatchEvent(new Event('input', { bubbles: true }))
    importTextarea.dispatchEvent(new Event('change', { bubbles: true }))

    if (typeof importForm.requestSubmit === 'function') {
      importForm.requestSubmit()
      return
    }

    importForm.submit()
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(generatedDraft, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      style={{
        marginBottom: 0,
        width: '100%',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        padding: '16px',
        background: '#fff',
      }}
    >
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); setExpanded(!expanded) }}
        style={{ fontWeight: 600, display: 'inline-block' }}
      >
        {expanded ? '▾' : '▸'} AI Generate Simulation
      </a>
      <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
        Generate a draft, review the JSON, then import it directly here or copy it manually.
      </div>
      {expanded && (
        <div style={{ marginTop: '10px' }}>
          <div className="form-group">
            <label style={{ fontWeight: 400 }}>Describe the simulation</label>
            <textarea
              rows={3}
              className="form-control"
              placeholder="e.g. A sorting exercise where students categorize lean six sigma waste types into value-added vs non-value-added"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label style={{ fontWeight: 400 }}>Plugin type</label>
            <select
              className="form-control"
              style={{ maxWidth: 280 }}
              value={pluginHint}
              onChange={(e) => setPluginHint(e.target.value)}
            >
              <option value="">Let AI decide</option>
              <option value="sorting">Sorting / Sequencing</option>
              <option value="interactive-path">Interactive Path</option>
              <option value="spreadsheet">Spreadsheet</option>
              <option value="statement-table">Statement Table</option>
              <option value="intro-card">Intro Card</option>
            </select>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
          >
            {loading ? 'Waiting on AI...' : 'Generate Draft'}
          </button>
          {loading && (
            <div
              className="mt-2"
              role="status"
              style={{
                marginBottom: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                background: '#0b5cab',
                color: '#fff',
                border: '1px solid #0b5cab',
                borderRadius: '4px',
                padding: '12px 14px',
              }}
            >
              <div
                className="icon icon_spinner10 icon_spin2"
                aria-hidden="true"
                style={{ fontSize: '18px', marginTop: '1px', flexShrink: 0, color: '#fff' }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>Generating your simulation draft.</div>
                <div>
                  This can take a little while while we wait for the AI response. Please do not navigate away
                  from this page until it finishes.
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="alert alert-danger mt-2" role="alert">{error}</div>
          )}
          {warnings.length > 0 && (
            <div className="alert alert-warning mt-2" role="alert">
              <strong>Notes:</strong>
              <ul style={{ marginBottom: 0, paddingLeft: '1.25rem' }}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {generatedDraft && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#495057' }}>
                Next steps:
                <ol style={{ marginBottom: 0, paddingLeft: '18px' }}>
                  <li>Review this generated JSON.</li>
                  <li>Click {importButtonLabel || 'Import as New'} to use the existing import flow.</li>
                  <li>Use Copy JSON only if you want to edit or save the draft yourself first.</li>
                </ol>
              </div>
              <textarea
                readOnly
                rows={10}
                className="form-control"
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                value={JSON.stringify(generatedDraft, null, 2)}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleCopy}
                >
                  {copied ? 'Copied!' : 'Copy JSON'}
                </button>
                <button
                  className="btn btn-myed btn-primary btn-primary-myed btn-sm"
                  onClick={handleImport}
                >
                  {importButtonLabel || 'Import as New'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
